/**
 * Form Data Handler
 * 
 * Manages club and pharmacy data for the Globo form submission
 * Listens for validation events and injects data before form submission
 */

import { CSS_CLASSES, API_ENDPOINTS } from '@form/constants';
import { STORAGE_KEYS, EVENTS, GLOBO_EVENTS, FORM_FIELDS, SELECTORS } from '@form/data-constants';
import { syncDataWithStorage, getStoredClubData, toggleHiddenClass } from '@form/utils';
import { removeAllDiscounts, clearClubAppliedFlag } from './club-validation.utils.js';
import { CLUB_VALIDATION_SELECTORS } from './club-validation.constants.js';
import { getGiftCardCartError } from './form-gift-card-validation.js';
import { fetchConfig } from '@theme/utilities';
import { updateOrderAttributes } from '@theme/order-attributes';

/**
 * @typedef {Object} ClubData
 * @property {string} club_id - Club identifier
 * @property {string} name - Club name
 * @property {boolean} pharmacy_pickup_payment - Whether pharmacy pickup payment is enabled
 * @property {number} discount_amount - Discount percentage amount
 * @property {string|false} club_content_page - Club content page URL or false
 */

/**
 * @typedef {Object} PharmacyData
 * @property {string} [id] - Pharmacy ID (Shopify metaobject ID)
 * @property {string} name - Pharmacy name
 * @property {string} streetname - Street address
 * @property {string} email - Contact email
 * @property {string} phone - Contact phone
 * @property {string} lat_lng - Coordinates (comma-separated)
 */

class FormDataHandler {
  /**
   * Stored club data
   * @type {ClubData|null}
   */
  #clubData = null;

  /**
   * Stored pharmacy data
   * @type {PharmacyData|null}
   */
  #pharmacyData = null;

  /**
   * Stored cart data
   * @type {Object|null}
   */
  #cartData = null;

  /**
   * Globo form wrapper reference
   * @type {HTMLElement|null}
   */
  #globoFormWrapper = null;

  /**
   * Error message element shown when club access is missing
   * @type {HTMLElement|null}
   */
  #clubAccessMessage = null;

  /**
   * Tracks whether we've dispatched after the Globo form finished loading
   * @type {boolean}
   */
  #hasDispatchedOnFormLoaded = false;

  constructor() {
    this.#initialize();
  }

  /**
   * Initialize event listeners
   * @private
   */
  #initialize() {
    // Restore data from sessionStorage immediately on initialization
    this.#restoreFromStorage();

    // Dispatch data ready event if we have data
    if (this.#clubData || this.#pharmacyData) {
      // Use setTimeout to ensure components are registered first
      setTimeout(() => {
        this.#dispatchDataReady();
      }, 0);
    }

    // Listen for club validation success
    document.addEventListener(EVENTS.CLUB_VALIDATED, (event) => {
      this.#handleClubValidated(event);
    });

    // Listen for pharmacy selection
    document.addEventListener(EVENTS.PHARMACY_SELECTED, (event) => {
      this.#handlePharmacySelected(event);
    });

    // Listen for cart data ready
    document.addEventListener(EVENTS.CART_DATA_READY, (event) => {
      this.#handleCartDataReady(event);
    });

    // Listen for Globo form loaded event
    document.addEventListener(GLOBO_EVENTS.FORM_LOADED, (event) => {
      this.#handleFormLoaded(event);
      this.#styleSubmitButton();
    });

    // Listen for Globo form before submit
    document.addEventListener(GLOBO_EVENTS.BEFORE_SUBMIT, (event) => {
      this.#handleBeforeSubmit(event);
    });

    // Listen for Globo form submitted successfully
    document.addEventListener(GLOBO_EVENTS.FORM_SUBMITTED, (event) => {
      this.#handleFormSubmitted(event);
    });

    document.addEventListener(EVENTS.CLUB_ACCESS_UPDATED, (event) => {
      this.#handleClubAccessUpdated(event);
    });
  }

  /**
   * Handle club validation success
   * @param {CustomEvent} event - Club validated event
   * @private
   */
  #handleClubValidated(event) {
    const clubData = event.detail?.clubData;

    if (!clubData) {
      return;
    }

    this.#clubData = clubData;

    // Store in sessionStorage for persistence across page navigation
    sessionStorage.setItem(STORAGE_KEYS.CLUB_DATA, JSON.stringify(clubData));

    // Check if all required data is now available and clear errors
    this.#checkAndClearErrors();

    // Dispatch event that data is ready
    this.#dispatchDataReady();

    this.#updateClubAccessState(true);
  }

  /**
   * Handle pharmacy selection
   * @param {CustomEvent} event - Pharmacy selected event
   * @private
   */
  #handlePharmacySelected(event) {
    const pharmacyData = event.detail?.pharmacyData;

    // Handle pharmacy data cleared (when "Change pharmacy" is clicked)
    if (!pharmacyData) {
      this.#pharmacyData = null;

      // Clear pharmacy fields from the form
      const form = document.querySelector('#product-filters-form, form[id*="form"], form');
      if (form) {
        this.#clearPharmacyFields(form);
      }

      // Dispatch data ready event to update UI
      this.#dispatchDataReady();
      return;
    }

    this.#pharmacyData = pharmacyData;

    // Store in sessionStorage
    sessionStorage.setItem(STORAGE_KEYS.PHARMACY_DATA, JSON.stringify(pharmacyData));

    // Check if all required data is now available and clear errors
    this.#checkAndClearErrors();

    // Dispatch event that data is ready
    this.#dispatchDataReady();
  }

  /**
   * Handle cart data ready
   * @param {CustomEvent} event - Cart data ready event
   * @private
   */
  #handleCartDataReady(event) {
    const cartData = event.detail?.cartData;

    if (!cartData) {
      return;
    }

    this.#cartData = cartData;

    // Check if all required data is now available and clear errors
    this.#checkAndClearErrors();
  }

  /**
   * Handle form loaded event
   * @param {CustomEvent} event - Form loaded event
   * @private
   */
  #handleFormLoaded(event) {
    // Try to restore data from sessionStorage
    this.#restoreFromStorage();

    // Dispatch data ready event only once after initial load
    if ((this.#clubData || this.#pharmacyData) && !this.#hasDispatchedOnFormLoaded) {
      this.#dispatchDataReady();
      this.#hasDispatchedOnFormLoaded = true;
    }

    this.#updateClubAccessState(this.#hasClubAccess());
  }

  /**
   * Handle before submit event
   * @param {CustomEvent} event - Before submit event
   * @private
   */
  #handleBeforeSubmit(event) {
    const form = event.detail?.form;

    if (!form) {
      return;
    }

    // Always sync with sessionStorage before validation to ensure data consistency
    // This prevents submission with stale data if sessionStorage was cleared
    const syncedData = syncDataWithStorage(
      { clubData: this.#clubData, pharmacyData: this.#pharmacyData },
      STORAGE_KEYS
    );
    this.#clubData = syncedData.clubData;
    this.#pharmacyData = syncedData.pharmacyData;

    // Validate all required data before submission
    const validationErrors = this.#validateRequiredData();

    if (validationErrors.length > 0) {
      // Add errors class to block-container to prevent Globo form submission
      const blockContainer = form.querySelector('.block-container');
      if (blockContainer) {
        blockContainer.classList.add('errors');
      }

      // Display validation errors
      this.#displayValidationErrors(validationErrors, form);

      return;
    }

    // Remove errors class if validation passes
    const blockContainer = form.querySelector('.block-container');
    if (blockContainer) {
      blockContainer.classList.remove('errors');
    }

    // Clear any previous validation error messages
    this.#clearValidationErrors();

    // Inject club and pharmacy data
    this.#injectFormData(form);
  }

  /**
   * Handle form submitted successfully
   * Clears sessionStorage, cart data, and discounts
   * @param {CustomEvent} event - Form submitted event
   * @private
   */
  async #handleFormSubmitted(event) {
    // Clear sessionStorage (club and pharmacy data)
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(STORAGE_KEYS.CLUB_DATA);
      sessionStorage.removeItem(STORAGE_KEYS.PHARMACY_DATA);
    }

    // Clear in-memory data
    this.#clubData = null;
    this.#pharmacyData = null;
    this.#cartData = null;

    // Clear cart using cart/clear endpoint
    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({}),
      });

      const response = await fetch(API_ENDPOINTS.CART_CLEAR, config);

      if (!response.ok) {
        throw new Error('Failed to clear cart');
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
    }

    // Remove discounts from cart and clear order attributes
    try {
      const discountComponent = document.querySelector(CLUB_VALIDATION_SELECTORS.discountComponent);
      if (discountComponent instanceof HTMLElement) {
        const sectionId = discountComponent.dataset.sectionId;
        if (sectionId) {
          // Clear order attributes (club_id and pharmacy_pickup_payment)
          // Force render to update UI to empty state after successful form submission
          await updateOrderAttributes(
            sectionId,
            {
              club_id: '',
              pharmacy_pickup_payment: '',
              discount_amount_percentage: '',
            },
            [],
            true // forceRender: true to update section after form submission
          );

          // Remove discounts from cart
          await removeAllDiscounts(sectionId);
        }
      }
      // Clear the club applied flag from discount component
      clearClubAppliedFlag();
    } catch (error) {
      console.error('Error removing discounts and clearing order attributes:', error);
    }
  }

  /**
   * Handle club access updates triggered outside this module
   * @param {CustomEvent} event
   * @private
   */
  #handleClubAccessUpdated(event) {
    if (!event || typeof event.detail?.hasAccess === 'undefined') {
      return;
    }

    const hasAccess = Boolean(event.detail.hasAccess);

    if (!hasAccess) {
      this.#clubData = null;
      this.#pharmacyData = null;
    }

    this.#updateClubAccessState(hasAccess);
  }

  /**
   * Determines whether we currently have club access
   * @returns {boolean}
   * @private
   */
  #hasClubAccess() {
    if (this.#clubData) {
      return true;
    }

    const storedClubData = getStoredClubData();

    if (storedClubData) {
      this.#clubData = storedClubData;
      return true;
    }

    return false;
  }

  /**
   * Toggles Globo form visibility and helper message based on club access
   * @param {boolean} hasAccess
   * @private
   */
  #updateClubAccessState(hasAccess) {
    if (!(this.#globoFormWrapper instanceof HTMLElement)) {
      this.#globoFormWrapper = document.querySelector(SELECTORS.GLOBO_FORM_APP);
    }

    if (!(this.#globoFormWrapper instanceof HTMLElement)) {
      return;
    }

    toggleHiddenClass(this.#globoFormWrapper, !hasAccess);

    const messageElement = this.#getOrCreateClubAccessMessage();

    if (!messageElement) {
      return;
    }

    if (hasAccess) {
      messageElement.textContent = '';
      messageElement.hidden = true;
      return;
    }

    const translations = window.Etixx?.translations?.pharmacist_flow || {};
    const fallbackMessage =
      translations.club_access_required ||
      translations.pharmacist_not_available ||
      'Please validate your club ID first.';

    messageElement.textContent = fallbackMessage;
    messageElement.hidden = false;
  }

  /**
   * Retrieves or creates the inline message used for club access errors
   * @returns {HTMLElement|null}
   * @private
   */
  #getOrCreateClubAccessMessage() {
    if (this.#clubAccessMessage instanceof HTMLElement) {
      return this.#clubAccessMessage;
    }

    let messageElement = document.querySelector(SELECTORS.CLUB_ACCESS_MESSAGE);

    if (!(messageElement instanceof HTMLElement)) {
      if (
        !(this.#globoFormWrapper instanceof HTMLElement) ||
        !(this.#globoFormWrapper.parentNode instanceof HTMLElement)
      ) {
        return null;
      }

      messageElement = document.createElement('p');
      messageElement.dataset.clubAccessMessage = 'true';
      messageElement.classList.add('club-access-message', CSS_CLASSES.ERROR);
      messageElement.hidden = true;
      messageElement.setAttribute('role', 'status');
      messageElement.setAttribute('aria-live', 'polite');

      this.#globoFormWrapper.parentNode.insertBefore(messageElement, this.#globoFormWrapper);
    }

    this.#clubAccessMessage = messageElement;
    return messageElement;
  }

  /**
   * Validate all required data before form submission
   * @returns {Array<string>} Array of validation error messages
   * @private
   */
  #validateRequiredData() {
    const errors = [];
    const translations = window.Etixx?.translations?.pharmacist_flow || {};

    // Check if cart has items
    if (!this.#cartData || !this.#cartData.items || this.#cartData.items.length === 0) {
      errors.push(translations.missing_cart_data || 'Cart is empty. Please add items to your cart.');
    }

    // Check if club ID is validated
    if (!this.#clubData) {
      errors.push(translations.missing_club_data || 'Club ID is required. Please validate your club ID.');
    }

    // Check if pharmacy is selected (only required when pharmacy_pickup_payment is true)
    if (this.#clubData?.pharmacy_pickup_payment === true && !this.#pharmacyData) {
      errors.push(translations.missing_pharmacy_data || 'Pharmacy selection is required. Please select a pharmacy.');
    }

    // Gift card product check (separate module — does nothing if setting unset)
    const giftCardError = getGiftCardCartError(this.#cartData);
    if (giftCardError) {
      errors.push(giftCardError);
    }

    return errors;
  }

  /**
   * Display validation errors to the user
   * @param {Array<string>} errors - Array of error messages
   * @param {HTMLFormElement} form - The form element
   * @private
   */
  #displayValidationErrors(errors, form) {
    const translations = window.Etixx?.translations?.pharmacist_flow || {};
    const errorTitle = translations.form_validation_error || 'Please complete the following before submitting:';

    // Create error message HTML using CSS classes from form-club-info.css
    const errorList = errors.map(error => `<li>${error}</li>`).join('');
    const errorHtml = `
      <div class="form-validation-errors" role="alert">
        <p class="form-validation-errors__title">${errorTitle}</p>
        <ul class="form-validation-errors__list">
          ${errorList}
        </ul>
      </div>
    `;

    // Find the submit button
    const submitButton = form.querySelector('button[type="submit"], .action.submit, .wizard__submit');

    if (!submitButton) {
      return;
    }

    // Find or create error container right after the submit button
    let errorContainer = submitButton.parentNode.querySelector('.form-validation-errors-container');

    if (!errorContainer) {
      // Create container after the submit button
      errorContainer = document.createElement('div');
      errorContainer.className = 'form-validation-errors-container';

      // Insert after the submit button
      if (submitButton.nextSibling) {
        submitButton.parentNode.insertBefore(errorContainer, submitButton.nextSibling);
      } else {
        submitButton.parentNode.appendChild(errorContainer);
      }
    }

    if (errorContainer) {
      errorContainer.innerHTML = errorHtml;

      // Scroll to error message
      errorContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /**
   * Clear validation error messages
   * @private
   */
  #clearValidationErrors() {
    const errorContainers = document.querySelectorAll('.form-validation-errors-container');
    errorContainers.forEach(container => {
      container.innerHTML = '';
    });
  }

  /**
   * Check if all required data is available and clear errors if so
   * @private
   */
  #checkAndClearErrors() {
    const validationErrors = this.#validateRequiredData();

    // If all data is now available, clear errors
    if (validationErrors.length === 0) {
      this.#clearValidationErrors();

      // Remove errors class from block-container
      const blockContainers = document.querySelectorAll('.block-container');
      blockContainers.forEach(container => {
        container.classList.remove('errors');
      });
    }
  }

  /**
   * Restore data from sessionStorage
   * @private
   */
  #restoreFromStorage() {
    try {
      const storedClubData = sessionStorage.getItem(STORAGE_KEYS.CLUB_DATA);
      const storedPharmacyData = sessionStorage.getItem(STORAGE_KEYS.PHARMACY_DATA);

      if (storedClubData) {
        this.#clubData = JSON.parse(storedClubData);
      }

      if (storedPharmacyData) {
        this.#pharmacyData = JSON.parse(storedPharmacyData);
      }
    } catch (error) {
      console.error('Error restoring data from storage:', error);
    }
  }


  /**
   * Inject club, pharmacy, and cart data into form
   * @param {HTMLFormElement} form - The form element
   * @private
   */
  #injectFormData(form) {
    if (!this.#clubData) {
      return;
    }

    // Inject club data
    this.#injectClubData(form, this.#clubData);

    // Inject pharmacy data if available, or clear pharmacy fields if not
    if (this.#pharmacyData) {
      this.#injectPharmacyData(form, this.#pharmacyData);
    } else {
      this.#clearPharmacyFields(form);
    }

    // Inject cart data if available
    if (this.#cartData) {
      this.#injectCartData(form, this.#cartData);
    }
  }

  /**
   * Inject club data fields
   * @param {HTMLFormElement} form - The form element
   * @param {ClubData} clubData - Club data to inject
   * @private
   */
  #injectClubData(form, clubData) {
    const clubFields = {
      [FORM_FIELDS.CLUB_ID]: clubData.club_id,
      [FORM_FIELDS.CLUB_NAME]: clubData.name,
      [FORM_FIELDS.CLUB_CONTENT_PAGE]: clubData.club_content_page || '',
      [FORM_FIELDS.PHARMACY_PICKUP_PAYMENT]: String(clubData.pharmacy_pickup_payment === true),
      [FORM_FIELDS.DISCOUNT_AMOUNT]: String(clubData.discount_amount || 0),
    };

    Object.entries(clubFields).forEach(([fieldName, fieldValue]) => {
      this.#createOrUpdateHiddenField(form, fieldName, fieldValue);
    });
  }

  /**
   * Inject pharmacy data fields
   * @param {HTMLFormElement} form - The form element
   * @param {PharmacyData} pharmacyData - Pharmacy data to inject
   * @private
   */
  #injectPharmacyData(form, pharmacyData) {
    const pharmacyFields = {
      [FORM_FIELDS.PHARMACY_ID]: pharmacyData.id || '',
      [FORM_FIELDS.PHARMACY_NAME]: pharmacyData.name,
      [FORM_FIELDS.PHARMACY_STREET]: pharmacyData.streetname,
      [FORM_FIELDS.PHARMACY_EMAIL]: pharmacyData.email,
      [FORM_FIELDS.PHARMACY_PHONE]: pharmacyData.phone,
      [FORM_FIELDS.PHARMACY_COORDS]: pharmacyData.lat_lng,
    };

    Object.entries(pharmacyFields).forEach(([fieldName, fieldValue]) => {
      this.#createOrUpdateHiddenField(form, fieldName, fieldValue);
    });
  }

  /**
   * Clear pharmacy data fields from form
   * @param {HTMLFormElement} form - The form element
   * @private
   */
  #clearPharmacyFields(form) {
    const pharmacyFieldNames = [
      FORM_FIELDS.PHARMACY_ID,
      FORM_FIELDS.PHARMACY_NAME,
      FORM_FIELDS.PHARMACY_STREET,
      FORM_FIELDS.PHARMACY_EMAIL,
      FORM_FIELDS.PHARMACY_PHONE,
      FORM_FIELDS.PHARMACY_COORDS,
    ];

    pharmacyFieldNames.forEach((fieldName) => {
      const field = form.querySelector(`input[name="${fieldName}"]`);
      if (field) {
        field.remove();
      }
    });
  }

  /**
   * Inject cart data for draft order creation
   * @param {HTMLFormElement} form - The form element
   * @param {Object} cartData - Cart data to inject
   * @private
   */
  #injectCartData(form, cartData) {
    // Get discount amount from club data
    const discountAmount = this.#clubData?.discount_amount || 0;
    const clubId = this.#clubData?.club_id || '';

    // Filter out free products (items with _free_product: "true" property)
    const filteredItems = (cartData.items || []).filter(item => !this.#isFreeProduct(item));

    // Prepare line items for draft order (excluding free products)
    const lineItems = filteredItems.map(item => {
      const lineItem = {
        variant_id: item.variant_id,
        product_id: item.product_id,
        sku: item.sku,
        title: item.product_title,
        variant_title: item.variant_title,
        quantity: item.quantity,
        price: item.price, // Price in cents
        line_price: item.line_price, // Total line price in cents
        properties: item.properties || {},
        gift_card: item.gift_card === true,
      };

      // Add appliedDiscount only if item is not a gift card and discount is available
      if (!lineItem.gift_card && discountAmount > 0 && clubId) {
        lineItem.appliedDiscount = {
          description: `${discountAmount}% off ${clubId}`,
          value: { percentage: discountAmount },
          valueType: 'PERCENTAGE',
        };
      }

      return lineItem;
    });

    // Calculate subtotal excluding free products
    const filteredSubtotal = filteredItems.reduce((sum, item) => {
      return sum + (typeof item.line_price === 'number' ? item.line_price : 0);
    }, 0);

    // Inject cart summary fields
    this.#createOrUpdateHiddenField(form, FORM_FIELDS.CART_ITEMS, JSON.stringify(lineItems));
    this.#createOrUpdateHiddenField(form, FORM_FIELDS.CART_SUBTOTAL, String(filteredSubtotal));
    this.#createOrUpdateHiddenField(form, FORM_FIELDS.CART_CURRENCY, cartData.currency);
  }

  /**
   * Create or update a hidden input field
   * @param {HTMLFormElement} form - The form element
   * @param {string} name - Field name
   * @param {string} value - Field value
   * @private
   */
  #createOrUpdateHiddenField(form, name, value) {
    let input = form.querySelector(`input[name="${name}"]`);

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }

    input.value = String(value);
  }

  /**
   * Dispatch data ready event
   * @private
   */
  #dispatchDataReady() {
    const event = new CustomEvent(EVENTS.FORM_DATA_READY, {
      detail: {
        clubData: this.#clubData,
        pharmacyData: this.#pharmacyData,
        cartData: this.#cartData,
      },
      bubbles: true,
    });

    document.dispatchEvent(event);
  }

  /**
   * Get current club data
   * @returns {ClubData|null}
   */
  getClubData() {
    return this.#clubData;
  }

  /**
   * Get current pharmacy data
   * @returns {PharmacyData|null}
   */
  getPharmacyData() {
    return this.#pharmacyData;
  }

  /**
   * Check if pharmacy is assigned to club
   * @returns {boolean}
   */
  hasPharmacy() {
    return this.#pharmacyData !== null;
  }

  /**
   * Clear all stored data
   */
  clearData() {
    this.#clubData = null;
    this.#pharmacyData = null;
    sessionStorage.removeItem(STORAGE_KEYS.CLUB_DATA);
    sessionStorage.removeItem(STORAGE_KEYS.PHARMACY_DATA);
  }

  /**
   * Style the Globo form submit button
   * @private
   */
  #styleSubmitButton() {
    // Wait for DOM to be ready
    setTimeout(() => {
      const submitButton = document.querySelector('.wizard__submit, button[type="submit"].submit');

      if (submitButton) {
        // Add skew button classes
        submitButton.classList.add('skew-button-primary', 'globo-submit-button');
      }
    }, 100);
  }

  /**
   * Check if an item is a free product (has _free_product: "true" property)
   * @param {Object} item - Cart item
   * @returns {boolean}
   * @private
   */
  #isFreeProduct(item) {
    if (!item || !item.properties) {
      return false;
    }

    // Check if properties object has _free_product set to "true"
    const freeProductValue = item.properties._free_product;
    return freeProductValue === 'true' || freeProductValue === true;
  }
}

// Initialize the handler
const formDataHandler = new FormDataHandler();

// Export for use in other modules
export default formDataHandler;

